import axios from '@/axios/axios-api'
import calcularFechas from '@/helpers/FechaDesdeMenorQueFechaHasta'
import clientCode from '@/helpers/getClientCode'
import isNativeDevice from '@/helpers/isNativeDevie'
import isMobile from '@/helpers/isMobile'
import { applyStoreSeo } from '@/helpers/seoHelper.js'
import { Device } from '@capacitor/device'

const actions = {
  setAppPreviousPage (context, previousPage) {
    context.commit('setPreviousPage', previousPage)
  },
  setAppFranquiciaSeleccionada (context, franquicia) {
    context.commit('setFranquiciaSeleccionada', franquicia)
  },
  setAppLoading (context, isLoading) {
    context.commit('setLoading', isLoading)
  },
  setAppGlobalMessage (context, message) {
    context.commit('setMessage', message)
  },
  async setAppLogo (context, code) {
    // const code = clientCode()
    const response = await axios.get(`api/clientes/${code}/logo`)
    context.commit('setLogo', response.data)
  },
  async setFranchiseLogo (context, franchiseLogo) {
    context.commit('setLogo', franchiseLogo)
  },
  async setDataClient (context, code) {
    // Validar que el código no esté vacío
    if (!code || String(code).trim() === '') {
      console.warn('setDataClient: código de cliente vacío, manteniendo datos existentes')
      return
    }

    try {
      // const code = clientCode()
      const response = await axios.get(`api/clientes/${code}/data`)

      // Validar que la respuesta tenga datos válidos
      if (!response || !response.data) {
        console.warn('setDataClient: respuesta inválida del servidor, manteniendo datos existentes')
        return
      }

      const {
        Name, CostoEnvio, FormasDeEnvio, FormasDePago, TextoEnvio, horariosRegulares,
        Telefono, SEO, EnviarVentasPorWhatsapp, UsarDeliveryPorZonas, UsarSeleccionHorario,
        NegocioCerrado, Descuentos,
        PrimaryColor, SecondaryColor, Lat, Long, Direccion
      } = response.data

      // Validar que los datos esenciales existan
      if (!Name || String(Name).trim() === '') {
        console.warn('setDataClient: nombre de cliente vacío en la respuesta, manteniendo datos existentes')
        return
      }

      const formasEnvio = FormasDeEnvio.map((e) => {
        let _name = e.fEntrega_Nombre
        if (e.DefaultText && e.DefaultText !== '') _name = `${_name} (${e.DefaultText})`
        return {
          default: e.Default,
          name: _name,
          id: e.fEntrega_id,
          minimo: e.ImporteMinimoCompra
        }
      })

      const formasPago = FormasDePago.map((e) => {
        return {
          name: e.fPago_Nombre,
          id: e.fPago_id,
          default: e.Default,
          PorcentajeAumento: e.PorcentajeAumento,
          credentials: e.credentials,
          Texto: e.Texto
        }
      })
      // Primero creo el horario regular del negocio
      // para mostrar dias y horarios para dar mayor información al cliente potencial o habitual
      let horarioRegular = null

      if (Array.isArray(horariosRegulares) && horariosRegulares.length > 0) {
        const diasSemana = {
          lunes: horariosRegulares.filter(hr => hr.filter(e => e.Id === 1).length > 0),
          martes: horariosRegulares.filter(hr => hr.filter(e => e.Id === 2).length > 0),
          miercoles: horariosRegulares.filter(hr => hr.filter(e => e.Id === 3).length > 0),
          jueves: horariosRegulares.filter(hr => hr.filter(e => e.Id === 4).length > 0),
          viernes: horariosRegulares.filter(hr => hr.filter(e => e.Id === 5).length > 0),
          sabado: horariosRegulares.filter(hr => hr.filter(e => e.Id === 6).length > 0),
          domingo: horariosRegulares.filter(hr => hr.filter(e => e.Id === 7).length > 0)
        }
        // con esta variable auxiliar muestro el horario que corresponde al dia
        const fechaAuxiliar = new Date(Date.now())
        // en javascript empieza a contar a partir del domingo como 0
        const diaActual = horariosRegulares.filter(hr => hr.filter(e => e.Id === (fechaAuxiliar.getDay() === 0 ? 7 : fechaAuxiliar.getDay())).length > 0)
        // infiero si el negocio está cerrado
        const negocioCerrado = diaActual.filter(e => e.filter(s => calcularFechas(fechaAuxiliar, s)).length > 0).length === 0
        // si en algun dia existe un array vacío entonces el negocio no atiende
        horarioRegular = {
          diasSemana,
          diaActual,
          negocioCerrado,
          dataOriginal: horariosRegulares
        }
      }

      const clientData = {
        Name,
        CostoEnvio,
        formasEnvio,
        formasPago,
        TextoEnvio,
        horarioRegular,
        Telefono,
        EnviarVentasPorWhatsapp,
        UsarDeliveryPorZonas,
        UsarSeleccionHorario: !!UsarSeleccionHorario,
        NegocioCerrado,
        Descuentos,
        PrimaryColor,
        SecondaryColor,
        Lat,
        Long,
        Direccion,
        SEO: SEO || null
      }

      const logo = context.state && context.state.logo
      applyStoreSeo({
        seo: SEO,
        name: Name,
        clientCode: code,
        image: logo || undefined
      })

      context.commit('setTiendaActual', Name)
      context.commit('setData', clientData)
    } catch (error) {
      // Si hay un error, no sobrescribir los datos existentes
      console.error('Error en setDataClient:', error)
      // No lanzar el error para que no se pierdan los datos del cliente
      // El componente que llama puede manejar el error si es necesario
      throw error
    }
  },
  setAppFranquicias (context, franquicias) {
    context.commit('setFranquicias', franquicias)
  },
  async setZonasCliente () {
    const code = clientCode()
    return await axios.get(`api/zonas/${code}`)
  },
  async setAppLogoFranquicia (context) {
    const code = clientCode()
    const response = await axios.get(`api/clientes/${code}/logo-franquicia`)
    context.commit('setLogoFranquicia', response.data)
  },
  async setFranchiseName (context, name) {
    context.commit('setTiendaActual', name)
  },
  async getTiendas (context) {
    if (context.state.customerPosition) {
      const { latitude, longitude } = context.state.customerPosition
      const response = await axios.post('api/clientes/tiendas/cercanas', {
        latitud: latitude,
        longitud: longitude
      })
      return response.data
    } else {
      const response = await axios.post('api/clientes/tiendas/todas')
      return response.data
    }
  },
  setAppCustomerPosition (context, position) {
    context.commit('setCustomerPosition', position)
  },
  async getFranquicias (context) {
    const { latitude, longitude } = context.state.customerPosition
    const franquicias = await axios.post('api/clientes/franquicias/cercanas', {
      latitud: latitude,
      longitud: longitude
    })
    return franquicias.data
  },
  async getSucursalesFranquicia (context, code) {
    if (context.state.customerPosition) {
      const { latitude, longitude } = context.state.customerPosition

      // Validar que las coordenadas sean válidas
      if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
        console.warn('Coordenadas inválidas, usando sucursales-all. Latitude:', latitude, 'Longitude:', longitude)
        const response = await axios.post(`api/clientes/franquicias/sucursales-all/${code}`)
        return response.data
      }

      console.log('Llamando a sucursales-cercanas con:', {
        latitud: latitude,
        longitud: longitude,
        franquicia: code
      })

      try {
        const response = await axios.post('api/clientes/franquicias/sucursales-cercanas', {
          latitud: latitude,
          longitud: longitude,
          franquicia: code
        })

        console.log('Respuesta de sucursales-cercanas:', {
          status: response.status,
          dataType: typeof response.data,
          isArray: Array.isArray(response.data),
          length: Array.isArray(response.data) ? response.data.length : 'N/A',
          data: response.data
        })

        // Si el endpoint devuelve un array vacío o null, hacer fallback a todas las sucursales
        const sucursales = response.data
        if (!sucursales || (Array.isArray(sucursales) && sucursales.length === 0)) {
          console.warn('sucursales-cercanas devolvió vacío, usando fallback a sucursales-all')
          const fallbackResponse = await axios.post(`api/clientes/franquicias/sucursales-all/${code}`)
          return fallbackResponse.data
        }

        return sucursales
      } catch (error) {
        console.error('Error al obtener sucursales cercanas:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          config: {
            url: error.config?.url,
            data: error.config?.data
          }
        })
        // Si hay error, hacer fallback a todas las sucursales
        const fallbackResponse = await axios.post(`api/clientes/franquicias/sucursales-all/${code}`)
        return fallbackResponse.data
      }
    } else {
      console.log('No hay posición del cliente, usando sucursales-all')
      const response = await axios.post(`api/clientes/franquicias/sucursales-all/${code}`)
      return response.data
    }
  },
  async savePedido (context, pedidoDTO) {
    const info = await Device.getInfo()
    const { platform } = info
    pedidoDTO.plataforma = isNativeDevice() ? platform : isMobile() ? 'Mobile' : 'Web'
    const response = await axios.post('/api/pedido/', pedidoDTO)
    return response
  },
  async getPedido (context, payload) {
    // Soporta tanto (id) como ({ id, clientCode, options })
    let id
    let clientCode = null
    let options = {}

    if (typeof payload === 'string' || typeof payload === 'number') {
      id = payload
    } else if (payload && typeof payload === 'object') {
      id = payload.id
      clientCode = payload.clientCode || null
      options = payload.options || {}
    } else {
      throw new Error('getPedido requiere un id válido')
    }

    const config = {}
    if (options.skipErrorRedirect) {
      config.skipErrorRedirect = true
    }
    if (clientCode) {
      config.params = { ...(config.params || {}), codigoCliente: clientCode }
    }
    const response = await axios.get(`api/pedido/Clickaway/${id}`, config)
    return response
  },
  async savePreferenciaMercadoPago (context, data) {
    const code = clientCode()
    // Usar siempre URL pública configurada; fallback al origin actual
    const configuredBase = process.env.VUE_APP_PUBLIC_BASE_URL
    const baseUrl = (configuredBase && configuredBase.trim() !== '')
      ? configuredBase
      : `${window.location.protocol}//${window.location.host}`
    const params = {
      PedidoId: data.id,
      ClientCode: code,
      BaseUrl: baseUrl,
      Total: data.total
    }
    const response = await axios.post(`api/mp/${code}/create_preference`, params)
    return response
  },
  async validarCupon (context, cupon) {
    const code = clientCode()
    const response = await axios.get(`api/clientes/${code}/validar-cupon?cupon=${cupon}`)
    return response
  },
  async setCupon (context, cupon) {
    context.commit('setCupon', cupon)
  }
}

export default actions
